import { Fragment, useCallback, useId, useRef } from 'react';
import cc from 'classnames';
import { observer } from 'decorators';
import { makeStyles, useAutoFocus, useDisposable } from 'hooks';
import theme from 'theme';
import { Typography, Paper, Stepper, Step, StepButton, StepContent, StepLabel, StepConnector, CardActions } from '@material-ui/core';
import Grid from 'components/grid';
import Spinner from 'components/spinner';
import Button from 'components/button';
import { CardListItem, CardListItemContent } from 'components/lists';
import Image from 'components/image';
import AddressMap from 'components/map';
import NavLink from 'components/navLink';
import AddressInput from 'components/form/address';
import Input, { Form } from 'components/form/input';
import Recaptcha from 'components/recaptcha';
import PdfEmbed from 'components/pdfEmbed';
import SaveButton from 'components/button/save';
import GridContainer from 'components/gridContainer';
import auth from 'services/auth';
import media from 'services/media';
import { splitWindowQuery } from 'utils/query';

import TimePickStep from './timePickStep';
import mapImage from './images/map.gif';
import speechImage from './images/speech.gif';
import typeImage from './images/calendar.png';
import clinicianImage from './images/person.png';

import AppView from '../app';
import SchedulerStore from './scheduler.store';

import { Alert, Download, InformationOutline } from 'mdi-material-ui';

import InsuranceSharedView, { InsurancePhotoId } from './insurance';

const useSchedulerStyles = makeStyles(theme => ({
  minHeight: {
    minHeight: '600px'
  },
  fullSize: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%'
  },
  errorMessage: {
    width: '400px',
    padding: theme.spacing(2),
    border: `2px solid ${theme.palette.error.main}`
  },
  fullWidth: {
    width: '100%'
  },
  scheduler: {
    backgroundColor: theme.palette.background.default
  },
  internal: {
    backgroundColor: (theme.palette.background.paper) + ' !important'
  },
  stepper: {
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(1)
    }
  },
  disable: {
    pointerEvents: 'none'
  },
  stepContent: {
    [theme.breakpoints.only('xs')]: {
      borderLeft: 'none',
      margin: theme.spacing(2, 0),
      padding: 0
    }
  },
  stepConnectorLine: {
    [theme.breakpoints.only('xs')]: {
      display: 'none'
    }
  },
  stepLabel: {
    textAlign: 'left'
  },
  stepStrong: {
    display: 'inline-block'
  },
  gridList: {
    listStyleType: 'none',
    padding: 0
  }
}));

export const FromWindowSchedulerView = observer(function FromWindowSchedulerView() {
  const store = useDisposable(() => {
    // Basic calendar view
    const windowQuery = splitWindowQuery().query;
    const { appType, appLoc, appPrac, gType, gLoc, gPrac, q, groupId, bId, bs, opts } = windowQuery;

    let optParsed = null;
    try { optParsed = JSON.parse(opts); } catch {}

    const getNumberIds = val => (val || '').split(',').map(c => !c || isNaN(c) ? null : Number(c)).filter(c => c != null);
    const getIds = val => (val || '').split(',').filter(c => c);

    return new SchedulerStore({
      appType: getNumberIds(appType),
      appLoc: getNumberIds(appLoc),
      appPrac: getNumberIds(appPrac),
      gType: getIds(gType),
      gLoc: getIds(gLoc),
      gPrac: getIds(gPrac),
      q: q === '1' ? true : (q === '0' ? false : null),
      groupId,
      bId,
      bs,
      opts: optParsed
    });
  }, []);

  return <AppView theme={store.getTheme()}>
    <GridContainer>
      <SchedulerView store={store} />
    </GridContainer>
  </AppView>;
});

const SchedulerView = observer(function SchedulerView({ store }) {
  const classes = useSchedulerStyles();

  const handleCancel = useCallback(() => store.cancelBooking().done(), [ store ]);

  if (auth.globalBlockingError || store.hadError || !store.isValid) {
    const desc = auth.globalBlockingError?.desc || store.errorMessage;
    return <Fragment>
      <Grid container direction="column" alignItems="center" spacing={0} className={cc(!store.internalMode && classes.fullSize)}>
        <Grid item xs />
        <Grid item>
          <Paper className={cc(classes.errorMessage, store.internalMode && classes.fullWidth)}>
            <Typography color="error" component="div">
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Alert />
                </Grid>
                <Grid item xs>
                  { !!desc && desc }
                  { !desc && store.hadError && 'There was an error when loading the scheduler, please try again.' }
                  { !desc && !store.hadError && !auth.isClinician && 'This scheduler is not available.' }
                  { !desc && !store.hadError && auth.isClinician && 'Online Scheduling must be set-up to use this feature.' }
                </Grid>
                { store.isRetry && store.hadError &&
                  <Grid item>
                    <Button onClick={handleCancel} isLoading={store.isCanceling}>Retry</Button>
                  </Grid>
                }
                { !desc && !store.hadError && auth.isClinician && auth.user.roles.isAdministrator &&
                  <Grid item>
                    <NavLink button color="primary" name="appointments.online">
                      Manage
                    </NavLink>
                  </Grid>
                }
              </Grid>
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs />
      </Grid>
      { !store.internalMode && <div className={classes.minHeight} /> }
    </Fragment>;
  }

  if (store.isLoading) {
    return <Fragment>
      <div className={cc(!store.internalMode && classes.fullSize)}><Spinner text="Loading Schedule..." /></div>
      { !store.internalMode && <div className={classes.minHeight} /> }
    </Fragment>;
  }

  return <MainSchedulerPart store={store} />;
});
export default SchedulerView;

const MainSchedulerPart = observer(function MainSchedulerPart({ store }) {
  const classes = useSchedulerStyles();
  const {
    currentStep,
    selectedTypeName,
    selectedLocationName,
    selectedLocationAddress,
    selectedClinicianName,
    selectedTimeName,
    shouldStopNav,
    blockTypeSelection,
    blockLocSelection,
    blockClinicianSelection,
    needsPaymentInfo,
    hasUpfrontPayment,
    hasClientSection,
    hideQuestions,
    hideClinicians,
    internalMode,
    timePick,
    hasSteped
  } = store;

  const setScroll = useCallback(r => store.setScrollElement(r), [ store ]);
  const handleQuestions = useCallback(() => store.goBackToStep(0), [ store ]);
  const handleService = useCallback(() => store.goBackToStep(1), [ store ]);
  const handleLocation = useCallback(() => store.goBackToStep(2), [ store ]);
  const handleClinician = useCallback(() => store.goBackToStep(3), [ store ]);
  const handleTime = useCallback(() => store.goBackToStep(4), [ store ]);
  const handleDetails = useCallback(() => store.goBackToStep(5), [ store ]);
  const handlePayment = useCallback(() => store.goBackToStep(6), [ store ]);
  const handleReceipt = useCallback(() => store.goBackToStep(7), [ store ]);

  const stepBaseId = useId();

  const steps = [
    {
      hide: hideQuestions,
      disabled: shouldStopNav,
      onClick: handleQuestions,
      label: <Fragment>
        Questions: {currentStep > 0 ? 'Completed' : null}
      </Fragment>,
      content: <Fragment>
        <br />
        <QuestionsStep store={store} />
      </Fragment>
    },
    {
      disabled: shouldStopNav || blockTypeSelection,
      onClick: handleService,
      label: <Fragment>
        Service: <strong className={classes.stepStrong}>{selectedTypeName}</strong>
      </Fragment>,
      content: <Fragment>
        <br />
        <ServiceStep store={store} isFirst={!hasSteped && hideQuestions} />
      </Fragment>
    },
    {
      disabled: shouldStopNav || blockLocSelection,
      onClick: handleLocation,
      label: <Fragment>
        Location: <strong className={classes.stepStrong}>{selectedLocationName}</strong>
        { selectedLocationAddress && <Typography variant="caption"><br />{selectedLocationAddress}</Typography> }
      </Fragment>,
      content: <Fragment>
        <br />
        <LocationStep store={store} isFirst={!hasSteped && hideQuestions && blockTypeSelection} />
      </Fragment>
    },
    {
      hide: hideClinicians,
      disabled: shouldStopNav || blockClinicianSelection,
      onClick: handleClinician,
      label: <Fragment>
        Practitioner: <strong className={classes.stepStrong}>{selectedClinicianName}</strong>
      </Fragment>,
      content: <Fragment>
        <br />
        <ClinicianStep store={store} isFirst={!hasSteped && hideQuestions && blockTypeSelection && blockLocSelection} />
      </Fragment>
    },
    {
      disabled: shouldStopNav,
      onClick: handleTime,
      label: <Fragment>
        Time: <strong className={classes.stepStrong}>{selectedTimeName}</strong>
      </Fragment>,
      content: <TimePickStep store={timePick} isFirst={!hasSteped && hideQuestions && blockTypeSelection && blockLocSelection && (hideClinicians || blockClinicianSelection)} />,
      onEntered: () => {
        if (hasSteped) {
          timePick.startFocus();
        }
      }
    },
    {
      hide: !hasClientSection,
      disabled: shouldStopNav,
      onClick: handleDetails,
      label: 'Your Details',
      content: <DetailsStep store={store} />
    },
    {
      hide: !needsPaymentInfo,
      disabled: shouldStopNav,
      onClick: handlePayment,
      label: 'Payment Details',
      content: <PaymentStep store={store} />
    },
    {
      hide: !hasUpfrontPayment,
      disabled: shouldStopNav,
      onClick: handleReceipt,
      label: 'Receipt',
      content: <InvoiceStep store={store} />
    },
    {
      label: 'Complete',
      content: <CompleteStep store={store} />
    }
  ].filter(s => !s.hide);

  return <Grid container direction="column" className={cc(classes.scheduler, internalMode && classes.internal)}>
    <div style={{ position: 'relative' }}>
      <div ref={setScroll} style={{ position: 'absolute', top: auth.hasMenuAccess ? -104 : 0 }} />
    </div>
    <Grid item component="nav" aria-label="Book Appointment">
      <Stepper
        activeStep={currentStep}
        orientation="vertical"
        className={cc(classes.stepper, internalMode && classes.internal)}
        connector={<StepConnector classes={{ line: classes.stepConnectorLine }} />}
        role="list"
      >
        { steps.map(({ disabled, onClick: handleClick, label, content, onEntered }, i) =>
          <Step key={i}>
            <StepButton
              focusRipple={!!handleClick}
              onClick={handleClick}
              className={cc(disabled && classes.disable)}
              role="listitem"
              aria-current={i === currentStep ? 'step' : undefined}
              aria-owns={stepBaseId + i}
              tabIndex={disabled ? '-1' : '0'}
            >
              <StepLabel className={classes.stepLabel}>
                { label }
              </StepLabel>
            </StepButton>
            <StepContent
              className={classes.stepContent}
              id={stepBaseId + i}
              role="group"
              aria-hidden={i === currentStep ? undefined : true}
              TransitionProps={{ onEntered: () => { store.scrollToView(); onEntered && onEntered(); } }}
            >
              { content }
            </StepContent>
          </Step>
        )}
      </Stepper>
    </Grid>
  </Grid>;
});

const CompleteStep = observer(function CompleteStep({ store }) {
  const { hasClientSection, needsPaymentInfo, isComplete, settings, internalMode, onConfirmReady } = store;

  if ((hasClientSection || needsPaymentInfo) && !settings.confirmation) { return null; }

  let Complete = null;
  if (!onConfirmReady && !isComplete && !hasClientSection && !needsPaymentInfo) {
    Complete = internalMode
      ?
      (
        <SaveButton variant="contained" color="primary" saveFunc={(p, e) => store.submitDetails(e)}>Confirm</SaveButton>
      )
      :
      (
        <Recaptcha
          ref={store._recaptchaRef}
          render={() => (
            <SaveButton variant="contained" color="primary" saveFunc={(p, e) => store.submitDetails(e)}>Confirm</SaveButton>
          )}
        />
      );
  }

  return <Fragment>
    { (hasClientSection || needsPaymentInfo || isComplete) &&
      <Fragment>
        <Typography variant="h6" paragraph>Appointment Scheduled</Typography>
        <div dangerouslySetInnerHTML={{ __html: settings.confirmation }} />
      </Fragment>
    }
    { !!Complete &&
      <Fragment>
        <Typography variant="h6" paragraph>Confirm Details</Typography>
        { Complete }
      </Fragment>
    }
  </Fragment>;
});

const QuestionsStep = observer(function QuestionsStep({ store }) {
  const { questionsForms, questionsValid, settings } = store;

  const saveFunc = useCallback((p, e) => store.saveQuestions(e), [ store ]);

  return <Grid container direction="column" spacing={2}>
    { questionsForms.map((q, i) => <QuestionItem key={i} question={q} store={store} />) }
    { !questionsValid &&
      <Grid item>
        <Typography color="error">
          <div dangerouslySetInnerHTML={{ __html: settings.noResultsFoundHtml || 'There are no available time slots for the options you have selected.' }} />
        </Typography>
      </Grid>
    }
    <Grid item>
      <SaveButton variant="contained" color="primary" disabled={!questionsValid} saveFunc={saveFunc}>Next</SaveButton>
    </Grid>
  </Grid>;
});

const QuestionItem = observer(function QuestionItem({ question: q, store }) {
  return <Grid item>
    <Typography>{ q.$('question').value }</Typography>
    <Input form={q} field="answer" />
  </Grid>;
});

const ServiceStep = observer(function ServiceStep({ store, isFirst }) {
  const classes = useSchedulerStyles();
  const { types } = store;

  const ref = useRef(null);
  useAutoFocus(ref, { enabled: () => !isFirst, deps: [ isFirst ] });

  return <Grid ref={ref} container alignItems="stretch" spacing={2} component="ul" className={classes.gridList}>
    { types.map(t => <ServiceItem key={t.id} store={store} model={t} />) }
  </Grid>;
});

const useServiceStyles = makeStyles(theme => ({
  imageBox: {
    width: '100%',
    paddingBottom: (900 / 16) + '%',
    position: 'relative',
    backgroundColor: theme.palette.grey[200]
  },
  imageContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    textAlign: 'center'
  },
  image: {
    width: '100%'
  },
  fallbackImage: {
    position: 'absolute',
    top: '18.89%',
    right: '32.5%',
    width: '35%',
    height: '62.22%'
  },
  flexContent: {
    display: 'flex'
  },
  description: {
    whiteSpace: 'pre-wrap',
    maxHeight: 400,
    overflow: 'auto',
    paddingRight: 8,
    marginRight: -8
  }
}));

const ServiceItem = observer(function ServiceItem({ store, model }) {
  const classes = useServiceStyles();
  const labelId = useId();
  let descId = useId();
  if (!model.titleDesc) { descId = undefined; }

  const { internalMode } = store;
  const handleSelect = useCallback(() => store.selectType(model), [ store, model ]);
  return <Grid component="li" item xs={12} sm={6} lg={internalMode ? 6 : 4} aria-labelledby={labelId} aria-describedby={descId}>
    <CardListItem>
      <div className={classes.imageBox}>
        <div className={classes.imageContainer}>
          <Image
            className={classes.image}
            fallbackComponent={<img src={typeImage} className={classes.fallbackImage} />}
            file={model.image ? `appointment/types/${model.id}/image/${model.image.fileSystemIndex}/${model.image.id}` : null}
            alt="Service Image"
          />
        </div>
      </div>
      <CardListItemContent className={classes.flexContent}>
        <Grid container direction="column" spacing={2}>
          <Grid item xs={model.description ? undefined : true}>
            <Typography variant="h6" id={labelId}>{model.name}</Typography>
            { model.titleDesc && <Typography variant="caption" id={descId}>{model.titleDesc}</Typography> }
          </Grid>
          { !internalMode &&
            <Grid item xs>
              { !!model.description && <Typography className={classes.description}>{model.description}</Typography> }
            </Grid>
          }
          <Grid item>
            <Typography align="center">
              <Button variant="contained" color="primary" onClick={handleSelect}>Select</Button>
            </Typography>
          </Grid>
        </Grid>
      </CardListItemContent>
    </CardListItem>
  </Grid>;
});

const LocationStep = observer(function LocationStep({ store, isFirst }) {
  const classes = useSchedulerStyles();
  const { availableLocations, isShowingAddress, showingAddressIndex } = store;

  const ref = useRef(null);
  useAutoFocus(ref, { enabled: () => !isFirst, deps: [ isFirst ] });

  return <Grid ref={ref} container alignItems="stretch" spacing={2} component="ul" className={classes.gridList}>
    { availableLocations.map((l, i) => <LocationItem key={l.id} store={store} model={l} index={i} isShowingEditor={isShowingAddress && showingAddressIndex === i} />) }
  </Grid>;
});

const useLocationItemStyles = makeStyles(theme => ({
  container: {
    backgroundColor: '#8fc8e5'
  },
  img: {
    width: '100%'
  },
  flexContent: {
    display: 'flex'
  }
}));

const LocationItem = observer(function LocationItem({ store, model, index, isShowingEditor }) {
  const classes = useLocationItemStyles();
  const labelId = useId();
  const descId = useId();

  const handleSave = useCallback(e => store.selectAddress(e), [ store ]);
  const handleCancel = useCallback(() => store.cancelAddress(), [ store ]);
  const handleSelect = useCallback(() => store.selectLocation(model, index), [ store, index, model ]);

  const { internalMode } = store;
  return <Grid component="li" item xs={12} sm={6} lg={internalMode ? 6 : 4} aria-labelledby={labelId} aria-describedby={descId}>
    { isShowingEditor &&
      <Form autoComplete onSubmit={handleSave} role="form">
        <CardListItem>
          <CardListItemContent>
            <Typography variant="h6">Your Address</Typography>
            <AddressInput autoComplete forceSmall form={store.address} field="address" />
          </CardListItemContent>
          <CardActions>
            <Button onClick={handleCancel}>Cancel</Button>
            <Grid item xs />
            <Button color="primary" type="submit">Save</Button>
          </CardActions>
        </CardListItem>
      </Form>
    }
    { !isShowingEditor &&
      <CardListItem>
        { !model.image &&
          <AddressMap
            className={classes.container}
            address={model.address}
            fallbackImg={model.isHomeVisit ? mapImage : speechImage}
            alt="Map Image"
          />
        }
        { model.image &&
          <Image
            fallbackSrc={model.isHomeVisit ? mapImage : speechImage}
            file={`settings/addresses/locations/${model.id}/image/${model.image.fileSystemIndex}/${model.image.id}`}
            className={classes.img}
            alt="Location Image"
          />
        }
        <CardListItemContent className={classes.flexContent}>
          <Grid container direction="column" spacing={2}>
            <Grid item xs>
              <Typography variant="h6" id={labelId}>{model.name}</Typography>
              <Typography variant="caption" id={descId}>{model.addressStr}</Typography>
            </Grid>
            <Grid item>
              <Typography align="center">
                <Button variant="contained" color="primary" onClick={handleSelect}>Select</Button>
              </Typography>
            </Grid>
          </Grid>
        </CardListItemContent>
      </CardListItem>
    }
  </Grid>;
});

const ClinicianStep = observer(function ClinicianStep({ store, isFirst }) {
  const classes = useSchedulerStyles();
  const { availableClinicians } = store;

  const ref = useRef(null);
  useAutoFocus(ref, { enabled: () => !isFirst, deps: [ isFirst ] });

  return <Grid ref={ref} container alignItems="stretch" spacing={2} component="ul" className={classes.gridList}>
    { availableClinicians.map(c => <ClinicianItem key={c.id} store={store} model={c} />) }
  </Grid>;
});

const useClinicianStyles = makeStyles(theme => ({
  imageBox: {
    width: '100%',
    paddingBottom: (900 / 16) + '%',
    position: 'relative',
    backgroundColor: theme.palette.grey[200]
  },
  imageContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    padding: theme.spacing(2)
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: '50%',
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.common.white
  },
  description: {
    whiteSpace: 'pre-wrap'
  },
  flexContent: {
    display: 'flex'
  }
}));

const ClinicianItem = observer(function ClinicianItem({ store, model }) {
  const labelId = useId();
  let descId = useId();
  const { internalMode } = store;
  if (internalMode || !model.description) { descId = undefined; }

  const classes = useClinicianStyles();
  const handleSelect = useCallback(() => store.selectClinician(model), [ store, model ]);

  return <Grid component="li" item xs={12} sm={6} lg={internalMode ? 6 : 4} aria-labelledby={labelId} aria-describedby={descId}>
    <CardListItem>
      <div className={classes.imageBox}>
        <div className={classes.imageContainer}>
          <Image
            className={classes.image}
            fallbackSrc={clinicianImage}
            file={model.image ? `clinicians/${model.id}/image/${model.image.fileSystemIndex}/${model.image.id}` : null}
            alt="Practitioner Image"
          />
        </div>
      </div>
      <CardListItemContent className={classes.flexContent}>
        <Grid container direction="column" spacing={2}>
          <Grid item id={labelId}>
            <Typography variant="h6">{model.name}</Typography>
            { model.qualifications && <Typography variant="caption">{model.qualifications}</Typography> }
          </Grid>
          { !internalMode &&
            <Grid item xs>
              { !!model.description && <Typography className={classes.description} id={descId}>{model.description}</Typography> }
            </Grid>
          }
          <Grid item>
            <Typography align="center">
              <Button variant="contained" color="primary" onClick={handleSelect}>Select</Button>
            </Typography>
          </Grid>
        </Grid>
      </CardListItemContent>
    </CardListItem>
  </Grid>;
});

const DetailsStep = observer(function DetailsStep({ store }) {
  const handleEdit = useCallback(e => store.onEditingInsurer(e), [ store ]);

  const ref = useRef(null);
  useAutoFocus(ref, { enabled: () => !!store.detailsForm, deps: [ store.detailsForm ] });

  if (!store.detailsForm) { return null; }

  const {
    isBooking,
    detailsForm: form,
    settings,
    hasInsuranceInfo,
    hideCopay,
    hasPhotoId,
    showAddressInfo,
    needsClientInfo
  } = store;

  const hasLegal = !!settings.legal;
  const isSmall = media.is('xs');

  return <Form ref={ref} key="form" autoComplete role="form">
    { isBooking && <Spinner text="Booking..." /> }
    <Grid container direction="column" spacing={2} style={{ display: isBooking ? 'none' : undefined }}>
      { needsClientInfo &&
        <Grid item>
          <Typography variant="h6">General Details</Typography>
          <Typography variant="subtitle1">We need some details about you.</Typography>
          <Input form={form} field="givenName" />
          <Input form={form} field="lastName" />
          <Input form={form} field="preferredName" />
          { (!settings.hideGender || !settings.hideDOB) &&
            <Grid container spacing={isSmall ? 0 : 2}>
              { !settings.hideDOB &&
                <Grid item xs={12} sm>
                  <Input form={form} field="dob" />
                </Grid>
              }
              { !settings.hideGender &&
                <Grid item xs={12} sm>
                  <Input form={form} field="gender" />
                </Grid>
              }
            </Grid>
          }
          { !settings.hideGenderIdentity &&
            <Grid container spacing={isSmall ? 0 : 2}>
              <Grid item xs={12} sm={9}>
                <Input form={form} field="genderIdentity" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Input form={form} field="pronouns" />
              </Grid>
            </Grid>
          }
          <Input form={form} field="email" />
          { !settings.hidePhone && <Input form={form} field="phone" /> }
          { (hasInsuranceInfo || hasPhotoId) && <br /> }
        </Grid>
      }
      { hasInsuranceInfo &&
        <Grid item>
          <InsuranceSharedView inline isTentative store={store} form={form} onEditingChanged={handleEdit} hideCopay={hideCopay} />
          { hasPhotoId && <br /> }
        </Grid>
      }
      { hasPhotoId &&
        <Grid item>
          <InsurancePhotoId inline isTentative form={form} />
        </Grid>
      }
      { showAddressInfo &&
        <Grid item>
          <Typography variant="h6">Your Address</Typography>
          <Typography variant="subtitle1" paragraph>Please enter your address details below.</Typography>
          <AddressInput autoComplete form={store.address} field="address" />
        </Grid>
      }
      { needsClientInfo &&
        <Grid item>
          <br />
          <Typography variant="h6">Additional Information</Typography>
          <div dangerouslySetInnerHTML={{ __html: settings.notesInfo }} />
          <Input form={form} field="notes" />
        </Grid>
      }
      { needsClientInfo && hasLegal &&
        <Grid item>
          <br />
          <div dangerouslySetInnerHTML={{ __html: settings.legal }} />
        </Grid>
      }
      { needsClientInfo && hasLegal &&
        <Grid item>
          <br />
          <Input form={form} field="sign" />
          <Input form={form} field="signName" />
        </Grid>
      }
      <BookSection store={store} validate />
    </Grid>
  </Form>;
});

const PaymentStep = observer(function PaymentStep({ store }) {
  const { isBooking } = store;

  return <Fragment>
    { isBooking && <Spinner text="Booking..." /> }
    <Grid container direction="column" spacing={2} style={{ display: isBooking ? 'none' : undefined }}>
      <BookSection store={store} />
    </Grid>
  </Fragment>;
});

const BookSection = observer(function BookSection({ store, validate }) {
  const {
    detailsForm: form,
    settings,
    bookButtonText,
    isRetry,
    isCanceling
  } = store;

  const handleCancel = useCallback(() => store.cancelBooking().done(), [ store ]);
  const saveFunc = useCallback((p, e) => store.submitDetails(e), [ store ]);
  return <Recaptcha
    ref={store._recaptchaRef}
    render={() => (
      <Fragment>
        { !isRetry && (!!settings.emailOptInEnabled || !!settings.smsOptInEnabled) &&
          <Grid item>
            <br />
            <Typography variant="h6">Consent</Typography>
            <Typography variant="subtitle1">To provide quality care, {auth.user.orgPublicName} uses {theme.brandName} as their {auth.isUsBilling && 'electronic medical record (EMR) & '}practice management solution.</Typography>
            { !!settings.emailOptInEnabled && <Input form={form} field="emailOptIn" margin="none" /> }
            { !!settings.smsOptInEnabled && <Input form={form} field="smsOptIn" margin="none" /> }
            <Input form={form} field="agreeOptIn" margin="none" />
          </Grid>
        }
        <Grid item>
          <Grid container spacing={1}>
            <Grid item xs />
            { isRetry &&
              <Grid item>
                <Button onClick={handleCancel} isLoading={isCanceling}>Cancel</Button>
              </Grid>
            }
            <Grid item>
              <SaveButton showGeneralErrors variant="contained" color="primary" saveFunc={saveFunc}>{bookButtonText}</SaveButton>
            </Grid>
          </Grid>
        </Grid>
      </Fragment>
    )}
  />;
});

const InvoiceStep = observer(function InvoiceStep({ store }) {
  const { bookingData } = store;
  const handleComplete = useCallback(() => store.complete(), [ store ]);

  const ref = useRef(null);
  useAutoFocus(ref, { enabled: () => !!bookingData, deps: [ bookingData ] });

  if (!bookingData) { return null; }

  const { invLink } = bookingData;

  return <Grid ref={ref} container direction="column" spacing={2}>
    <Grid item>
      <Typography variant="h6">Payment Confirmation</Typography>
      <Typography variant="subtitle1">Thank you for your payment.</Typography>
    </Grid>
    <Grid item>
      <Grid container spacing={2}>
        <Grid item xs>
          <NavLink button href={invLink + '&download=true'} target="_blank" startIcon={<Download />}>Download</NavLink>
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" onClick={handleComplete}>Complete</Button>
        </Grid>
      </Grid>
    </Grid>
    <Grid item>
      <PdfEmbed url={invLink} aria-label="Invoice Preview">
        <Typography align="right">
          <InformationOutline /> PDF previewing is not enabled in this browser
        </Typography>
      </PdfEmbed>
    </Grid>
  </Grid>;
});